import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import ReactSwitch from "react-switch";

import { templateStore } from "@pages/Applications/C&C/C&CSetup/templateStore";
import LanguageTable from "../../../Common/LanguageTable";
import { SectionDef, SWITCH_PROPS_SMALL, TYPE_LABELS } from "../constants";

interface Props {
    basePath: string[];
    formData: any;
    defaultFormTitle: Record<string, string>;
    defaultFormSubtitle: Record<string, string>;
    sectionDefs: SectionDef[];
    mainColor: string;
    updatePath: (path: string[], value: any) => void;
}

export function FormFieldsSection({
    basePath,
    formData,
    defaultFormTitle,
    defaultFormSubtitle,
    sectionDefs,
    mainColor,
    updatePath,
}: Props) {
    const { t } = useTranslation();
    const snap = useSnapshot(templateStore);

    const getData = useCallback(
        (subPath: string[]) => {
            try {
                return subPath.reduce(
                    (acc: any, key) => acc?.[key],
                    snap.template
                );
            } catch {
                return undefined;
            }
        },
        [snap.template]
    );

    return (
        <div
            data-testid="form-fields-section"
            style={{
                marginBottom: 24,
                padding: 16,
                border: "1px solid #e0e0e0",
                borderRadius: 8,
            }}
        >
            <h6
                style={{
                    fontWeight: 700,
                    fontSize: 15,
                    marginBottom: 16,
                    color: mainColor,
                    textTransform: "uppercase",
                }}
            >
                {t("Form fields")}
            </h6>

            {/* Form title & subtitle */}
            <LanguageTable
                path={[...basePath]}
                fields={[
                    {
                        key: "FormTitle",
                        label: "Form title",
                        placeholder: "Enter form title",
                        isVisible: formData?.FormTitleVisible ?? true,
                        onVisibilityToggle: () =>
                            updatePath(
                                [...basePath, "FormTitleVisible"],
                                !(formData?.FormTitleVisible ?? true)
                            ),
                    },
                    {
                        key: "FormSubtitle",
                        label: "Form subtitle",
                        placeholder: "Enter subtitle",
                        isVisible: formData?.FormSubtitleVisible ?? true,
                        onVisibilityToggle: () =>
                            updatePath(
                                [...basePath, "FormSubtitleVisible"],
                                !(formData?.FormSubtitleVisible ?? true)
                            ),
                    },
                ]}
                defaultTexts={{
                    FormTitle: defaultFormTitle,
                    FormSubtitle: defaultFormSubtitle,
                }}
                sectionTitle={t("Form header")}
            />

            {/* Sections with fields */}
            {sectionDefs.map((section) => (
                <SectionFieldsTable
                    key={section.key}
                    section={section}
                    basePath={basePath}
                    mainColor={mainColor}
                    getData={getData}
                    updatePath={updatePath}
                />
            ))}
        </div>
    );
}

/* ── Single section table ── */

function SectionFieldsTable({
    section,
    basePath,
    mainColor,
    getData,
    updatePath,
}: {
    section: SectionDef;
    basePath: string[];
    mainColor: string;
    getData: (path: string[]) => any;
    updatePath: (path: string[], value: any) => void;
}) {
    const { t } = useTranslation();

    return (
        <div
            data-testid={`section-fields-${section.key}`}
            style={{
                marginTop: 20,
                padding: 12,
                background: "#fafafa",
                borderRadius: 8,
                border: "1px solid #eee",
            }}
        >
            <h6
                style={{
                    fontWeight: 600,
                    fontSize: 14,
                    marginBottom: 12,
                    color: mainColor,
                }}
            >
                {t(section.titleKey)}
            </h6>

            <table
                style={{
                    width: "100%",
                    fontSize: 13,
                    tableLayout: "fixed",
                    borderCollapse: "collapse",
                }}
            >
                <colgroup>
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                    <col style={{ width: "20%" }} />
                </colgroup>
                <thead>
                    <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                        <th style={{ padding: "8px 12px", textAlign: "left" }}>
                            {t("Field")}
                        </th>
                        <th style={{ padding: "8px 12px", textAlign: "center" }}>
                            {t("Type")}
                        </th>
                        <th style={{ padding: "8px 12px", textAlign: "center" }}>
                            {t("Required")}
                        </th>
                        <th style={{ padding: "8px 12px", textAlign: "center" }}>
                            {t("Enabled")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {section.fields.map((field) => {
                        const fieldPath = [
                            ...basePath,
                            "Sections",
                            section.key,
                            "Fields",
                            field.key,
                        ];
                        const fieldData = getData(fieldPath) || {};
                        const isRequired =
                            fieldData?.Required ?? field.defaultRequired;
                        const isEnabled = fieldData?.Activer ?? true;

                        return (
                            <tr
                                key={field.key}
                                data-testid={`field-row-${field.key}`}
                                style={{
                                    borderBottom: "1px solid #f0f0f0",
                                    opacity: isEnabled ? 1 : 0.5,
                                }}
                            >
                                <td
                                    style={{
                                        padding: "10px 12px",
                                        fontWeight: 500,
                                    }}
                                >
                                    {t(field.labelKey)}
                                </td>
                                <td
                                    style={{
                                        padding: "10px 12px",
                                        textAlign: "center",
                                        color: "#888",
                                    }}
                                >
                                    {TYPE_LABELS[field.type] || field.type}
                                </td>
                                <td
                                    style={{
                                        padding: "10px 12px",
                                        textAlign: "center",
                                    }}
                                >
                                    <ReactSwitch
                                        data-testid={`field-required-switch-${field.key}`}
                                        {...SWITCH_PROPS_SMALL}
                                        checked={isRequired}
                                        onChange={() =>
                                            updatePath(
                                                [...fieldPath, "Required"],
                                                !isRequired
                                            )
                                        }
                                    />
                                </td>
                                <td
                                    style={{
                                        padding: "10px 12px",
                                        textAlign: "center",
                                    }}
                                >
                                    <ReactSwitch
                                        data-testid={`field-enabled-switch-${field.key}`}
                                        {...SWITCH_PROPS_SMALL}
                                        checked={isEnabled}
                                        onChange={() =>
                                            updatePath(
                                                [...fieldPath, "Activer"],
                                                !isEnabled
                                            )
                                        }
                                    />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
